import * as ScratchBlocks from 'scratch-blocks';
import computedStyleToInlineStyle from 'computed-style-to-inline-style';
import {withAssistantPreviewMenus} from './blocks';

const xml = ScratchBlocks.utils.xml;
const MAX_OPERATIONS = 12;
const assistantGroups = new Map();

export const routeAssistantBlockEvent = (vm, event) => {
    const target = assistantGroups.get(event.group);
    if (target) target.blocks.blocklyListen(event);
    else vm.blockListener(event);
};

export const captureAssistantGameProject = vm => {
    const target = vm.editingTarget;
    const targets = vm.runtime.targets.filter(item => item.isOriginal).map(item => ({
        id: item.id,
        name: item.getName(),
        isStage: item.isStage,
        costumes: item.getCostumes().map(costume => ({name: costume.name})),
        sounds: item.getSounds().map(sound => ({name: sound.name})),
        variables: Object.values(item.variables).map(variable => ({
            id: variable.id,
            name: variable.name,
            type: variable.type
        })),
        scripts: item.blocks._scripts.map(id => ({id, xml: item.blocks.blockToXML(id)}))
    }));
    const extensionChecks = {
        text2speech: 'text2speech_speakAndWait',
        videoSensing: 'videoSensing_videoOn',
        faceSensing: 'faceSensing_whenFaceDetected',
        pen: 'pen_penDown',
        translate: 'translate_getTranslate',
        music: 'music_playNoteForBeats'
    };
    const extensions = Object.entries(extensionChecks)
        .filter(([id, blockType]) => vm.extensionManager?.isExtensionLoaded(id) && ScratchBlocks.Blocks[blockType])
        .map(([id]) => id);
    const projectContext = {
        selectedTargetId: target?.id || targets.find(item => item.isStage)?.id,
        extensions,
        targets
    };
    // Leave room for the prompt and request envelope under the API's 256 KiB body limit.
    if (new TextEncoder().encode(JSON.stringify(projectContext)).length > 244 * 1024) {
        throw new Error('PROJECT_TOO_LARGE');
    }
    return {projectContext};
};

export const captureAssistantProject = vm => {
    const target = vm.editingTarget;
    if (!target || target.isStage || !target.isOriginal) throw new Error('SELECT_SPRITE');
    return {targetId: target.id, targetName: target.getName(), ...captureAssistantGameProject(vm)};
};

const parseScript = value => {
    if (typeof value !== 'string' || value.length > 50000 || /<!DOCTYPE|<!ENTITY/i.test(value)) {
        throw new Error('INVALID_BLOCKS');
    }
    let root;
    try {
        root = xml.textToDom(value);
    } catch {
        throw new Error('INVALID_BLOCKS');
    }
    if (root.localName !== 'block' || root.hasAttribute('shadow')) throw new Error('INVALID_BLOCKS');
    const allowed = new Set(['block', 'shadow', 'field', 'value', 'statement', 'next', 'mutation', 'arg']);
    for (const element of [root, ...root.querySelectorAll('*')]) {
        if (!allowed.has(element.localName)) throw new Error('INVALID_BLOCKS');
        if (element.localName === 'arg' && element.parentElement?.localName !== 'mutation') {
            throw new Error('INVALID_BLOCKS');
        }
        if ((element.localName === 'block' || element.localName === 'shadow') &&
            !ScratchBlocks.Blocks[element.getAttribute('type')]) throw new Error('INVALID_BLOCKS');
        for (const attribute of element.attributes) {
            if (/^on/i.test(attribute.name) || /^(href|style|src)$/i.test(attribute.name)) {
                throw new Error('INVALID_BLOCKS');
            }
        }
        if ((element.localName === 'block' || element.localName === 'shadow') && element.hasAttribute('id')) {
            element.removeAttribute('id');
        }
    }
    return root;
};

const validateOperations = operations => {
    if (!Array.isArray(operations) || operations.length < 1 || operations.length > MAX_OPERATIONS) {
        throw new Error('INVALID_BLOCKS');
    }
    return operations.map(operation => {
        if (!operation || !['add', 'replace', 'delete'].includes(operation.action)) {
            throw new Error('INVALID_BLOCKS');
        }
        if (operation.action !== 'add' && (typeof operation.scriptId !== 'string' || !operation.scriptId)) {
            throw new Error('INVALID_BLOCKS');
        }
        if (operation.action === 'delete' && 'xml' in operation) throw new Error('INVALID_BLOCKS');
        if (operation.action !== 'delete' && typeof operation.xml !== 'string') throw new Error('INVALID_BLOCKS');
        return {...operation, root: operation.action === 'delete' ? null : parseScript(operation.xml)};
    });
};

export const validateImportedAssistantFields = (element, block) => {
    const numeric = /^math_(number|integer|whole_number|positive_number|angle)$/.test(element.getAttribute('type'));
    const numericField = [...element.children].find(field =>
        field.localName === 'field' && field.getAttribute('name') === 'NUM');
    if (numeric && (!numericField || !numericField.textContent?.trim() ||
        !Number.isFinite(Number(numericField.textContent)))) {
        throw new Error('INVALID_BLOCKS');
    }
    if (!block && element.localName === 'shadow') return;
    for (const field of element.children) {
        if (field.localName !== 'field') continue;
        const requested = field.hasAttribute('id') ? field.getAttribute('id') : field.textContent;
        if (field.getAttribute('name') === 'NUM' &&
            (!requested?.trim() || !Number.isFinite(Number(requested)))) throw new Error('INVALID_BLOCKS');
        const imported = block?.getField(field.getAttribute('name'));
        if (!imported || (numeric && field.getAttribute('name') === 'NUM' ?
            Number(imported.getValue()) !== Number(requested) : String(imported.getValue()) !== requested)) {
            throw new Error('INVALID_BLOCKS');
        }
    }
};

export const importValidatedRoot = (workspace, root, validate = true) => {
    const doc = xml.textToDom(`<xml xmlns="http://www.w3.org/1999/xhtml">${xml.domToText(root)}</xml>`);
    const blocks = [...doc.querySelectorAll('block, shadow')];
    for (const block of blocks) block.setAttribute('id', ScratchBlocks.utils.idGenerator.genUid());
    const ids = ScratchBlocks.Xml.domToWorkspace(doc, workspace);
    if (ids.length !== 1) throw new Error('INVALID_BLOCKS');
    if (validate) {
        for (const element of blocks) {
            validateImportedAssistantFields(element, workspace.getBlockById(element.getAttribute('id')));
        }
    }
    return ids[0];
};

const applyToWorkspace = (workspace, operations) => {
    const changed = [];
    const used = new Set();
    for (const operation of operations) {
        if (operation.action !== 'add') {
            if (used.has(operation.scriptId)) throw new Error('INVALID_BLOCKS');
            used.add(operation.scriptId);
            const block = workspace.getBlockById(operation.scriptId);
            if (!block || block.getParent()) throw new Error('SCRIPT_CHANGED');
            block.dispose(false);
        }
        if (operation.root) {
            changed.push(importValidatedRoot(workspace, operation.root));
        }
    }
    return changed;
};

export const restoreAssistantScripts = (workspace, scripts) => {
    const doc = xml.textToDom(`<xml xmlns="http://www.w3.org/1999/xhtml">${scripts.map(script => script.xml).join('')}</xml>`);
    for (const block of workspace.getTopBlocks(false)) block.dispose(false);
    ScratchBlocks.Xml.domToWorkspace(doc, workspace);
};

const inSandbox = (callback, previewMenus) => {
    const liveWorkspace = ScratchBlocks.getMainWorkspace();
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-10000px;top:0;width:1200px;height:1000px;visibility:hidden';
    document.body.appendChild(container);
    let workspace;
    ScratchBlocks.Events.disable();
    return withAssistantPreviewMenus(previewMenus, () => {
        try {
            workspace = ScratchBlocks.inject(container, {
                readOnly: true,
                toolbox: '<xml></xml>',
                theme: liveWorkspace.getTheme(),
                media: liveWorkspace.options.pathToMedia,
                comments: false,
                collapse: false,
                sounds: false,
                trashcan: false,
                modalInputs: false
            });
            return callback(workspace);
        } finally {
            workspace?.dispose();
            container.remove();
            if (liveWorkspace) ScratchBlocks.common.setMainWorkspace(liveWorkspace);
            ScratchBlocks.Events.enable();
        }
    });
};

const variablesOf = workspace => workspace.getVariableMap().getAllVariables()
    .map(variable => ({id: variable.getId(), name: variable.name, type: variable.type}))
    .sort((a, b) => a.id.localeCompare(b.id));

export const prepareAssistantVariables = (workspace, operations) => {
    const variables = variablesOf(workspace);
    const byId = new Map(variables.map(variable => [variable.id, variable]));
    const names = new Set(variables.map(variable => `${variable.type}:${variable.name.toLowerCase()}`));
    for (const operation of operations) {
        for (const field of operation.root?.querySelectorAll('field[id]') || []) {
            const id = field.getAttribute('id');
            const name = field.textContent;
            const fieldName = field.getAttribute('name');
            const type = fieldName === 'VARIABLE' ? '' : fieldName === 'LIST' ? 'list' :
                fieldName === 'BROADCAST_OPTION' ? 'broadcast_msg' : null;
            const variable = byId.get(id);
            if (variable) {
                if (variable.name !== name || (type !== null && variable.type !== type)) {
                    throw new Error('INVALID_BLOCKS');
                }
                continue;
            }
            if (type === null || !id || id.length > 100 || !name?.trim() || name.length > 100 ||
                names.has(`${type}:${name.toLowerCase()}`)) throw new Error('INVALID_BLOCKS');
            workspace.createVariable(name, type, id);
            byId.set(id, {id, name, type});
            names.add(`${type}:${name.toLowerCase()}`);
        }
    }
    return JSON.stringify(variablesOf(workspace));
};

export const renderAssistantPlan = async operations => {
    const parsed = validateOperations(operations);
    const main = ScratchBlocks.getMainWorkspace();
    if (!main) throw new Error('EDITOR_UNAVAILABLE');
    const projectXml = ScratchBlocks.Xml.workspaceToDom(main);
    const images = inSandbox(workspace => {
        ScratchBlocks.clearWorkspaceAndLoadFromXml(projectXml, workspace);
        const variablesAfterPreparation = prepareAssistantVariables(workspace, parsed);
        const deleted = parsed.filter(operation => operation.action === 'delete')
            .map(operation => workspace.getBlockById(operation.scriptId))
            .map(block => {
                if (!block || block.getParent()) throw new Error('SCRIPT_CHANGED');
                return renderBlockImage(block);
            });
        const ids = applyToWorkspace(workspace, parsed);
        if (JSON.stringify(variablesOf(workspace)) !== variablesAfterPreparation) throw new Error('INVALID_BLOCKS');
        return [...deleted, ...ids.map(id => renderBlockImage(workspace.getBlockById(id)))];
    });
    return Promise.all(images.map(inlineSvgImages));
};

const planScriptsForTarget = (snapshot, target) => {
    const original = snapshot.projectContext.targets.find(item => item.id === target.targetId);
    const existing = new Map((original?.scripts || []).map(script => [script.id, script.xml]));
    const used = new Set();
    return target.scripts.map(script => {
        if (script.scriptId !== null) {
            if (!existing.has(script.scriptId) || used.has(script.scriptId)) throw new Error('INVALID_BLOCKS');
            used.add(script.scriptId);
        } else if (script.xml === null) throw new Error('INVALID_BLOCKS');
        return {root: parseScript(script.xml === null ? existing.get(script.scriptId) : script.xml),
            validate: script.xml !== null};
    });
};

const previewMenusForTarget = (snapshot, plan, target) => {
    const original = snapshot.projectContext.targets.find(item => item.id === target.targetId);
    const stage = plan.targets.find(item => item.isStage);
    const originalStage = snapshot.projectContext.targets.find(item => item.isStage);
    const names = items => [...new Set(items.map(item => item.name).filter(Boolean))];
    const costumes = names([...(original?.costumes || []),
        ...target.assets.filter(asset => asset.kind === (target.isStage ? 'backdrop' : 'costume'))]);
    const backdrops = names([...(originalStage?.costumes || []),
        ...(stage?.assets || []).filter(asset => asset.kind === 'backdrop')]);
    return {
        costumes,
        backdrops,
        sounds: names(original?.sounds || []),
        sprites: plan.targets.filter(item => !item.isStage).map(item => item.name),
        targetName: target.name,
        isStage: target.isStage
    };
};

const loadPlanTarget = (workspace, snapshot, plan, target) => {
    const stage = plan.targets.find(item => item.isStage);
    const declarations = [...(stage?.variables || []), ...target.variables, ...plan.broadcasts];
    const seen = new Map();
    for (const variable of declarations) {
        if (seen.has(variable.id)) {
            const previous = seen.get(variable.id);
            if (previous.name !== variable.name || previous.type !== variable.type) throw new Error('INVALID_BLOCKS');
            continue;
        }
        seen.set(variable.id, variable);
        workspace.createVariable(variable.name, variable.type, variable.id);
    }
    const roots = planScriptsForTarget(snapshot, target);
    for (const {root} of roots) {
        for (const field of root.querySelectorAll('field[id]')) {
            const variable = seen.get(field.getAttribute('id'));
            if (!variable || variable.name !== field.textContent) throw new Error('INVALID_BLOCKS');
        }
    }
    return roots.map(({root, validate}) => importValidatedRoot(workspace, root, validate));
};

export const validateAssistantGamePlan = (snapshot, plan) => {
    if (!plan || !Array.isArray(plan.targets) || !Array.isArray(plan.broadcasts)) throw new Error('INVALID_BLOCKS');
    const current = snapshot.projectContext.targets;
    const existing = new Set(current.map(target => target.id));
    const returned = new Set(plan.targets.filter(target => target.targetId !== null).map(target => target.targetId));
    if (returned.size !== existing.size || [...existing].some(id => !returned.has(id)) ||
        plan.targets.filter(target => target.isStage).length !== 1) throw new Error('INVALID_BLOCKS');
    for (const target of plan.targets) {
        if (!Array.isArray(target.scripts) || !Array.isArray(target.variables) || !Array.isArray(target.assets)) {
            throw new Error('INVALID_BLOCKS');
        }
        inSandbox(workspace => loadPlanTarget(workspace, snapshot, plan, target),
            previewMenusForTarget(snapshot, plan, target));
    }
};

export const renderAssistantGamePlanTarget = async (snapshot, plan, key) => {
    const target = plan.targets.find(item => item.key === key);
    if (!target) throw new Error('INVALID_BLOCKS');
    const images = inSandbox(workspace => loadPlanTarget(workspace, snapshot, plan, target)
        .map(id => renderBlockImage(workspace.getBlockById(id))), previewMenusForTarget(snapshot, plan, target));
    return Promise.all(images.map(inlineSvgImages));
};

const renderBlockImage = block => {
    if (!block) throw new Error('INVALID_BLOCKS');
    computedStyleToInlineStyle(block.getSvgRoot(), {
        recursive: true,
        properties: ['fill', 'font-family', 'font-size', 'font-weight']
    });
    const group = block.getSvgRoot().cloneNode(true);
    group.innerHTML = group.innerHTML.replace(/&nbsp;/g, ' ');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.appendChild(group);
    document.body.appendChild(svg);
    const bounds = group.getBoundingClientRect();
    svg.setAttribute('width', String(Math.max(1, bounds.width + 20)));
    svg.setAttribute('height', String(Math.max(1, bounds.height + 20)));
    svg.setAttribute('viewBox', `0 0 ${Math.max(1, bounds.width + 20)} ${Math.max(1, bounds.height + 20)}`);
    group.setAttribute('transform', 'translate(10 10)');
    const serialized = new XMLSerializer().serializeToString(svg);
    svg.remove();
    return serialized;
};

const inlineSvgImages = async serialized => {
    const doc = new DOMParser().parseFromString(serialized, 'image/svg+xml');
    const imageElements = [...doc.querySelectorAll('image')];
    await Promise.all(imageElements.map(async element => {
        const href = element.getAttribute('href') || element.getAttribute('xlink:href');
        if (!href || href.startsWith('data:')) return;
        const url = new URL(href, document.baseURI);
        if (url.origin !== location.origin) throw new Error('INVALID_BLOCKS');
        const response = await fetch(url.href, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('INVALID_BLOCKS');
        const blob = await response.blob();
        const data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('INVALID_BLOCKS'));
            reader.readAsDataURL(blob);
        });
        element.setAttribute('href', data);
        element.removeAttribute('xlink:href');
    }));
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(doc))}`;
};

export const applyAssistantCode = (vm, snapshot, operations) => {
    const target = vm.editingTarget;
    if (!target || target.id !== snapshot.targetId || target.isStage || !target.isOriginal) {
        throw new Error('SCRIPT_CHANGED');
    }
    const original = snapshot.projectContext.targets.find(item => item.id === snapshot.targetId);
    if (!original) throw new Error('SCRIPT_CHANGED');
    const currentScripts = target.blocks._scripts.map(id => ({id, xml: target.blocks.blockToXML(id)}));
    const restoreScripts = JSON.stringify(currentScripts) !== JSON.stringify(original.scripts);
    const main = ScratchBlocks.getMainWorkspace();
    if (!main) throw new Error('EDITOR_UNAVAILABLE');
    const parsed = validateOperations(operations);
    // Validate the entire batch before creating an undoable event group on the live workspace.
    inSandbox(workspace => {
        ScratchBlocks.clearWorkspaceAndLoadFromXml(ScratchBlocks.Xml.workspaceToDom(main), workspace);
        if (restoreScripts) restoreAssistantScripts(workspace, original.scripts);
        const variablesAfterPreparation = prepareAssistantVariables(workspace, parsed);
        applyToWorkspace(workspace, parsed);
        if (JSON.stringify(variablesOf(workspace)) !== variablesAfterPreparation) throw new Error('INVALID_BLOCKS');
    });
    ScratchBlocks.Events.setGroup(true);
    const group = ScratchBlocks.Events.getGroup();
    assistantGroups.set(group, vm.editingTarget);
    try {
        if (restoreScripts) restoreAssistantScripts(main, original.scripts);
        prepareAssistantVariables(main, parsed);
        applyToWorkspace(main, parsed);
        main.cleanUp();
    } finally {
        ScratchBlocks.Events.setGroup(false);
    }
};
